import Component from 'flarum/common/Component';

/**
 * ============================================================================
 * 无限滚动 + IntersectionObserver —— **唯一实现** (v0.19.25 · 批 43c)
 * ============================================================================
 *
 * ## v0.19.25 · 批 43c: 唯一实现搬到 common/
 *   本文件 = 改前 `js/src/forum/utils/InfiniteScroll.js` 的**全部内容** (连注释一起搬)
 *   **外加**一个 `scrollState()` 钩子。
 *   动机: 批 41 只统一了 **forum 侧** 4 个文件, **admin 侧** (回收站) 自己又抄了一份
 *   (findTrashSentinel / findTrashScrollRoot / disconnectTrashObserver /
 *    syncTrashObserver / scheduleTrashSync / loadTrashNextPage) ⇒ 仍是「改一处要记得改另一处」。
 *   ⇒ 现在 forum 侧 4 个子类 + admin 回收站共用**这一份**基类。
 *
 *   ⚠️ `js/src/forum/utils/InfiniteScroll.js` 保留为 re-export (只为不破坏 4 个 forum 子类的
 *      import 路径 ⇒ 那 4 个文件零改动); admin 侧直接 import 本文件。
 *
 *   ⚠️ 产物里必然是**两份拷贝** (forum.js 一份 + admin.js 一份) —— Flarum 只有 forum / admin
 *      两个独立 entry, 没有 common entry ⇒ JS 打包的必然结果, **不是** bug。
 *
 * ## ⭐ 唯一的「数据侧」差异点 = scrollState()
 *   forum 侧是 **flat state** (this.meta / this.loading / this.loadingMore) ⇒ 默认实现即可。
 *   admin 侧是 **per-kind state** (this.trashStateFor(kind)) ⇒ admin **覆写** scrollState()。
 *   ⇒ 基类里 syncObserver / loadNextPage 一律读 `this.scrollState()`, 别的什么都不用管。
 *
 *   ⚠️ `scrollState()` 也必须被 mixin (withInfiniteScroll) 装到父类不是 Component 的组件上 ——
 *      StudentLessonsModal 走的就是 mixin, 而它的 `loadNextPage` 用的是**基类那份**
 *      (它只覆写了 findScrollRoot / syncObserver) ⇒ 不装的话 `this.scrollState()` 直接 TypeError。
 *      ⇒ 见下方 SHARED_METHODS 表里的 'scrollState'。
 * ============================================================================
 *
 * ============================================================================
 * 无限滚动 + IntersectionObserver —— 共用基类 (批 41 · 段① 提取)
 * ============================================================================
 *
 * ## 为什么抽成共用入口
 *   这套机制原来在 **4 个文件里各抄了一份**:
 *     components/LessonsTab.js / components/OperationLogsTab.js /
 *     components/SalesTab.js / modal/StudentLessonsModal.js
 *   ⚠️ 抄的是**踩过坑的版本** —— v0.17.16a / v0.17.20 修过的 bug 全记在下面这些注释里:
 *     ① body / documentElement 被误当滚动根 ⇒ **不滚动就把所有页一次翻完**
 *     ② 本仓 m.redraw 不同步更新 DOM ⇒ observer **一次都建不出来** (滚到底纹丝不动)
 *     ③ 孤儿 observer 泄漏 (实测 20 次开关泄漏 19 个)
 *   ⇒ 本仓铁律「一个逻辑一个入口, 不许抄两遍」: 改一处, 另外 3 处必须跟着改。
 *   ⇒ 以前抄了 4 份 ⇒ 某处被后人「简化」了, 修好的 bug 就在那一处复发。
 *
 * ## 子类必须提供 (基类**不给默认值**, 忘了给就是静默失效)
 *   - this.sentinelAttr  哨兵 DOM 属性名 (如 'data-zlessonsid')
 *   - this.meta (含 has_more) / this.loading / this.loadingMore / this.offset
 *   - this.redraw() / this.fetchPage(offset, mode) / this._destroyed
 *
 * ## 各子类的差异 (参数化或覆写, ⛔ 不许"顺手统一")
 *   - 哨兵属性名 → this.sentinelAttr (4 个文件各不相同)
 *   - sid 生成逻辑 → **必须留在各自文件** (tabSeq / modalSeq 是两个计数器, 统一会撞号)
 *   - StudentLessonsModal 的 findScrollRoot / syncObserver 是 **v0.17.15 旧版实现**
 *     (无 body 排除 / 无 scrollHeight 判定 / 无 _destroyed 闸)
 *     ⇒ 本批**只搬不改**, 该文件里以覆写形式保留; 统一它属**批 43**。
 *
 * ## 批 43 之前的红线 (⛔ 别动)
 *   ⛔ 别"简化"掉 findScrollRoot 里排除 body / documentElement 的那段
 *   ⛔ 别"优化"掉 syncObserver 里「等下一帧再重试」的逻辑
 *   ⛔ 别删 syncObserver 开头的 if (this._destroyed) return;
 * ============================================================================
 */
export default class InfiniteScrollBase extends Component {
  /**
   * 找到**属于本实例**的 sentinel 元素 (哨兵)。
   *
   * ⚠️ 哨兵属性名从 this.sentinelAttr 取 (各 tab / 弹层各不相同), **基类不给默认值**。
   */
  findSentinel() {
    // ⚠️⚠️ **必须走 document 全局查, 不能用 this.element.querySelector** (v0.17.16 实测踩到)
    //   一开始我图「安全」写成先查 this.element —— 结果 tab 卸载后 this.element 仍指向**已脱离文档**
    //   的那棵旧 DOM 树, 从里面照样查得到 sentinel, 于是「切走后才回来的翻页请求」在 finally 里
    //   调 syncObserver() 时会给这个**永远不会再滚动、也没人会再 disconnect 它**的节点建 observer。
    //   实测 10/07 上开关 20 次: created 164 / disconnected 145 / **live 19**, 一个周期漏一个。
    //   document 查询天然匹配不到已脱离文档的节点 —— v0.17.15 弹层用全局查 + _sid 认领正是这个原因。
    //   (再叠一道 this._destroyed 闸, 见 onremove 与 syncObserver 开头)
    return document.querySelector('[' + this.sentinelAttr + '="' + this._sid + '"]');
  }

  /**
   * 从 sentinel 往上找**真正会滚**的那个祖先。
   *
   * 只认 overflow-y 为 auto/scroll **且 scrollHeight > clientHeight** 的元素 ——
   * 'visible' 不滚, 'hidden' 是裁切不是滚动, 「声明了 overflow 但内容还没撑出滚动条」同样不滚。
   * 找不到就返回 null (= 用视口作 root), 宁可退化也不要崩。
   *
   * ⚠️⚠️ **body / documentElement 必须显式排除** (v0.17.16a 实测根因, 别"简化"掉这段)
   *   本组件是页面里的**普通区块**, 祖先链上全是 overflow-y:visible, **一个可滚容器都没有**,
   *   最后落到 body —— 而 Flarum/Bootstrap 给 body 写了 `overflow-y: scroll`, 只认 overflow-y 的
   *   老逻辑(只认 overflow-y)就把 body 选走了。可 body **自己根本不滚**: 实测 sH === cH
   *   (整篇文档 24389 时 body 仍是 24389/24389)。真正在滚的是 documentElement 的 scrollport,
   *   也就是**视口**。
   *   ⇒ root=body 时 IntersectionObserver 的 root 覆盖**整篇文档**而不是视口
   *      (实测 rootBounds.height 2261 → 27959 一路随内容涨), sentinel 永远「相交」
   *      ⇒ 每加载一页重建 observer 又立刻相交 ⇒ **无滚动也把 17 页全翻完**
   *        (实测 17 个请求, scrollY 恒 0)。
   *   ⇒ 排除 body/documentElement 后返回 null **不是降级兜底, 而是几何正确的值**:
   *      视口 scrollport 与 documentElement 的滚动盒是同一个东西。
   *
   * 与弹层的区别: 弹层里真正滚的是 .ModalManager; 本 tab 真正滚的是 window (root=null 即视口)。
   * 所以这里不写死任何类名, 实测为准。
   */
  findScrollRoot(el) {
    let node = el && el.parentElement;
    while (node) {
      if (node !== document.body && node !== document.documentElement) {
        const ov = window.getComputedStyle(node).overflowY;
        if ((ov === 'auto' || ov === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
      }
      node = node.parentElement;
    }
    return null;
  }
  disconnectObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  /**
   * 当前这次滚动要用的分页状态 —— ⭐ 唯一的「数据侧」差异点。
   *
   * 默认实现 = **forum 侧现状** (flat state: this.meta / this.loading / this.loadingMore)
   * ⇒ forum 侧 4 个子类**一个字都不用改** (B1)。
   * admin 侧是 per-kind state ⇒ admin **覆写**本方法返回
   *   `this.trashStateFor(this.trashTab)` 的那三个字段。
   *
   * ⚠️ 只被基类的 syncObserver / loadNextPage 读取 ⇒ 覆写它**不会**改任何别的行为。
   * ⚠️ 基类的 loadNextPage 判闸读本方法的返回值, 但置位仍写 `this.loadingMore`
   *    (flat state ⇒ 两者是同一个字段) ⇒ per-kind state 的组件必须**同时覆写 loadNextPage**
   *    (admin 侧就是这么做的)。
   */
  scrollState() {
    return {
      meta: this.meta,
      loading: this.loading,
      loadingMore: this.loadingMore
    };
  }

  /**
   * (重新)挂 observer。
   *
   * ⚠️⚠️ **为什么必须等下一帧** (v0.17.15 实测踩到, 别"优化"掉)
   *   本仓库的 `m.redraw` 不是原生 mithril redraw —— Flarum 包装过它
   *   (实测: 手动删掉 sentinel 再 m.redraw(), 紧接着同步查 DOM **仍然是旧的**)。
   *   所以「redraw(); syncObserver();」这种同一段代码里紧挨着的写法,
   *   syncObserver 跑的时候 sentinel 还没进 DOM → 提前 return → observer **一次都建不出来**
   *   (调试表现: 全程只有 1 个网络请求, 滚到底纹丝不动)。
   *   ⇒ 找不到 sentinel 时推到下一帧重试, 并且**限次** (最多 5 次),
   *      否则 has_more=true 但 sentinel 迟迟不出现时会变成 60fps 空转。
   *
   * @param {number}  tries  已重试次数 (内部传递, 调用方不用管)
   */
  syncObserver(tries) {
    if (tries === void 0) {
      tries = 0;
    }
    // ⚠️ 组件已卸载 → 绝不再建 observer (见 findSentinel 注释里实测到的 19 个 live 泄漏)
    if (this._destroyed) return;
    this.disconnectObserver();
    const st = this.scrollState();
    const meta = st.meta;
    if (!meta || !meta.has_more) return; // has_more=false ⇒ 不装, 自然不再发请求
    if (st.loading || st.loadingMore) return;
    const sentinel = this.findSentinel();
    if (!sentinel) {
      if (tries < 5) this.scheduleSync(tries + 1);
      return;
    }
    this._observer = new IntersectionObserver(entries => {
      // isIntersecting 而不是 entry.isIntersecting 单条 —— 一次回调可能给多条
      if (entries.some(e => e.isIntersecting)) this.loadNextPage();
    }, {
      root: this.findScrollRoot(sentinel),
      // 提前 200px 就开始拉: 等 sentinel 真的滚进视口再加载会明显卡一下
      rootMargin: '0px 0px 200px 0px',
      threshold: 0
    });
    this._observer.observe(sentinel);
  }

  /** 下一帧再试一次挂 observer (见 syncObserver 的时序注释) */
  scheduleSync(tries) {
    if (this._syncRaf) return; // 已经排过一次, 不重复排
    this._syncRaf = requestAnimationFrame(() => {
      this._syncRaf = null;
      this.syncObserver(tries);
    });
  }

  /**
   * 触底: 追加下一批。
   *
   * (LessonsTab 那份的补充: 搜索词照旧带上 ⇒ 滚动追加仍在同一个关键词范围内。)
   */
  loadNextPage() {
    const st = this.scrollState();
    const meta = st.meta;
    // 三道闸门 —— 任一不满足都不发请求 (has_more=false 后不再发)
    if (!meta || !meta.has_more) return;
    if (st.loading || st.loadingMore) return;
    this.loadingMore = true;
    this.disconnectObserver(); // 加载中先拆, 防止回调连发
    this.redraw();
    this.fetchPage(this.offset, 'append');
  }
}

/**
 * 共用方法的 7 个名字 —— withInfiniteScroll() 按这张表把基类方法装到别的父类上。
 *
 * ⚠️ v0.19.25 · 批 43c — 表里多了 'scrollState':
 *   StudentLessonsModal 走的是 withInfiniteScroll(Modal), 而它的 `loadNextPage` 用的是**基类那份**
 *   (它只覆写了 findScrollRoot / syncObserver, 没覆写 loadNextPage)
 *   ⇒ 基类 loadNextPage 改读 `this.scrollState()` 之后, mixin 装的这份也必须有 scrollState
 *   ⇒ 不装的话 StudentLessonsModal 触底时直接 `TypeError: this.scrollState is not a function`。
 *   ⇒ 装的是**基类默认实现** (flat state) ⇒ 与改前逐字等价, 行为零变化。
 */
const SHARED_METHODS = ['findSentinel', 'findScrollRoot', 'disconnectObserver', 'syncObserver', 'scheduleSync', 'loadNextPage', 'scrollState'];

/**
 * 给**父类不是 Component** 的组件用的 mixin。
 *
 * ## 为什么需要它
 *   本仓的 StudentLessonsModal 的父类是 Flarum 的 **Modal** (不是 Component) ——
 *   Modal 带着标题栏 / 关闭按钮 / 焦点陷阱 / 遮罩等一整套行为, **不能丢**
 *   (丢了就是弹层整个坏掉: 没标题、没关闭键、Esc 无效)。
 *   ⇒ JavaScript 单继承, 没法同时「继承 Modal」和「继承 InfiniteScrollBase」。
 *   ⇒ 这里把基类那 7 个方法**原样装**到 Modal 的子类原型上 (同一个函数对象, 不是复制一份逻辑)。
 *
 * ## 覆写依然有效
 *   装到**父类**原型上 ⇒ 子类自己定义的方法 (own property) 优先 ⇒
 *   StudentLessonsModal 的 legacy findScrollRoot / syncObserver 照旧生效。
 */
export function withInfiniteScroll(Base) {
  class InfiniteScrollMixin extends Base {}
  SHARED_METHODS.forEach(name => {
    Object.defineProperty(InfiniteScrollMixin.prototype, name, {
      value: InfiniteScrollBase.prototype[name],
      writable: true,
      configurable: true,
      enumerable: false
    });
  });
  return InfiniteScrollMixin;
}
flarum.reg.add('ziiven-ziven-scheduler', 'common/InfiniteScroll', InfiniteScrollBase);